import React, { useEffect, useState } from "react";
import { CustomButton } from "../../atoms/CustomButton";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { StatusBadge } from "../../atoms/StatusBadge";
import { Surface } from "../../atoms/Surface";
import {
  HiCheck,
  HiInformationCircle,
  HiOfficeBuilding,
  HiPhone,
  HiRefresh,
  HiShieldCheck,
  HiTrendingUp,
} from "react-icons/hi";
import useAuth from "../../../hooks/useAuth";
import { useGetEntityUser } from "../../../hooks/users";
import SecureStorage from "../../../utils/secureStorage";
import { usePermissions } from "../../../utils/permissions";
import {
  CurrencyType,
  formatPrice,
  getCurrencyFromCountryCode,
} from "../../../utils/currency";

const ROIGuarantee = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const permissions = usePermissions(user);

  const [isGuaranteeMode, setIsGuaranteeMode] = useState(true);

  const { data: ownerProfile } = useGetEntityUser({
    customer_identification: user.user_identification,
    entityIdentification: user.entity_identification,
    user_identification: user.user_identification,
  });

  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyType>("USD");

  useEffect(() => {
    setSelectedCurrency(
      getCurrencyFromCountryCode(ownerProfile?.user_country_code)
    );
  }, [ownerProfile?.user_country_code]);

  const formattedPrice = formatPrice(250, selectedCurrency);

  const getROIGuaranteePlan = () => {
    switch (selectedCurrency) {
      case "CHF":
        return "SUMOC_ROIGUA250CHF";
      case "EUR":
        return "SUMOC_ROIGUA250EUR";
      case "USD":
      default:
        return "SUMOC_ROIGUA250USD";
    }
  };

  const handlePaymentRedirect = () => {
    const planIdentification = getROIGuaranteePlan();
    SecureStorage.setSelectedPlan(planIdentification);

    const paymentUrl = `${process.env.REACT_APP_PAYMENT_SERVICE}/payment/charge?template_image_url=https://cdn.1cdn.app/application/UMD/2405110558455_logo.png&redirect_page=${process.env.REACT_APP_HOST_URL}/control-panel/roi-guarantee/success&user_identification=${user.user_identification}&payment_plan_identification=${planIdentification}&language_code=en&entity_identification=${user.entity_identification}`;

    window.location.href = paymentUrl;
  };

  const renderFeatureList = (text: string) => {
    const lines = text.split("\n").filter((line) => line.trim());
    return (
      <div className="space-y-2">
        {lines.map((line, index) => (
          <div key={index} className="flex items-start gap-2">
            <HiCheck className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-gray-600">{line.trim()}</span>
          </div>
        ))}
      </div>
    );
  };

  const features = isGuaranteeMode
    ? [
        {
          title: t("dashboard.roi_guarantee.features.full_refund"),
          description: t("dashboard.roi_guarantee.features.full_refund_desc", {
            price: formattedPrice,
          }),
          icon: <HiRefresh className="w-6 h-6 text-blue-600" />,
        },
        {
          title: t("dashboard.roi_guarantee.features.guaranteed_roi"),
          description: t(
            "dashboard.roi_guarantee.features.guaranteed_roi_desc"
          ),
          icon: <HiTrendingUp className="w-6 h-6 text-green-600" />,
        },
        {
          title: t("dashboard.roi_guarantee.features.implementation_guidance"),
          description: t(
            "dashboard.roi_guarantee.features.implementation_guidance_desc"
          ),
          icon: <HiPhone className="w-6 h-6 text-blue-600" />,
        },
        {
          title: t("dashboard.roi_guarantee.features.priority_support"),
          description: t(
            "dashboard.roi_guarantee.features.priority_support_desc"
          ),
          icon: <HiShieldCheck className="w-6 h-6 text-blue-600" />,
        },
      ]
    : [
        {
          title: t("dashboard.roi_guarantee.setup_pack_features.expert_setup"),
          description: t(
            "dashboard.roi_guarantee.setup_pack_features.expert_setup_desc"
          ),
          icon: <HiPhone className="w-6 h-6 text-blue-600" />,
        },
        {
          title: t(
            "dashboard.roi_guarantee.setup_pack_features.support_60_days"
          ),
          description: t(
            "dashboard.roi_guarantee.setup_pack_features.support_60_days_desc"
          ),
          icon: <HiShieldCheck className="w-6 h-6 text-blue-600" />,
        },
      ];

  return (
    <div className="roi-guarantee-page">
      <Helmet>
        <title>{t("dashboard.roi_guarantee.title")} | Super User Manager</title>
        <meta
          name="description"
          content={t("dashboard.roi_guarantee.description") || ""}
        />
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_HOST_URL}/control-panel/roi-guarantee`}
        />
      </Helmet>

      <div className="container mx-auto py-6 pt-20">
        <div className="text-center mb-8">
          {/* design-ok: titre hero d'une page marketing */}
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t("dashboard.roi_guarantee.title")}
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            {isGuaranteeMode
              ? t("dashboard.roi_guarantee.subtitle")
              : t("dashboard.roi_guarantee.setup_pack_subtitle")}
          </p>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            {isGuaranteeMode
              ? t("dashboard.roi_guarantee.description")
              : t("dashboard.roi_guarantee.setup_pack_description")}
          </p>
        </div>

        <div className="flex justify-center mb-4">
          <div className="bg-gray-100 p-1 rounded-lg">
            <div className="flex">
              <button
                onClick={() => setIsGuaranteeMode(false)}
                className={`px-6 py-3 rounded-md text-sm font-medium transition-all duration-200 ${
                  !isGuaranteeMode
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {t("dashboard.roi_guarantee.setup_pack")}
              </button>
              <button
                onClick={() => setIsGuaranteeMode(true)}
                className={`px-6 py-3 rounded-md text-sm font-medium transition-all duration-200 ${
                  isGuaranteeMode
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {t("dashboard.roi_guarantee.guarantee_pack")}
              </button>
            </div>
          </div>
        </div>

        {isGuaranteeMode && !permissions.hasFeatureAccess("ROI_GUARANTEE") ? (
          <div className="flex justify-center mb-8">
            <StatusBadge color="warning" size="sm">
              {t("dashboard.roi_guarantee.annual_only")}
            </StatusBadge>
          </div>
        ) : !permissions.hasFeatureAccess("ROI_GUARANTEE") ? (
          <div className="mb-8"></div>
        ) : (
          <div className="mb-8"></div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {isGuaranteeMode && (
              <Surface className="bg-blue-50 border-blue-200">
                <div className="text-center">
                  <HiShieldCheck className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                    {t("dashboard.roi_guarantee.risk_free_protection")}
                  </h2>
                  <p className="text-gray-700 mb-4">
                    {t("dashboard.roi_guarantee.guarantee_description")}
                  </p>
                  <div className="flex items-center justify-center gap-8 text-sm text-gray-600">
                    <div className="text-center">
                      <div className="font-bold text-2xl text-blue-600">
                        300%
                      </div>
                      <div>{t("dashboard.roi_guarantee.roi_label")}</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-2xl text-green-600">
                        90
                      </div>
                      <div>{t("dashboard.roi_guarantee.days_label")}</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-2xl text-blue-600">
                        100%
                      </div>
                      <div>{t("dashboard.roi_guarantee.refund_label")}</div>
                    </div>
                  </div>
                </div>
              </Surface>
            )}

            {!isGuaranteeMode && (
              <Surface className="bg-blue-50 border-blue-200">
                <div className="text-center">
                  <HiPhone className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                    {t("dashboard.roi_guarantee.setup_pack_expert_success")}
                  </h2>
                  <p className="text-gray-700 mb-4">
                    {t("dashboard.roi_guarantee.setup_pack_expert_description")}
                  </p>
                  <div className="flex items-center justify-center gap-8 text-sm text-gray-600">
                    <div className="text-center">
                      <div className="font-bold text-2xl text-blue-600">2</div>
                      <div>
                        {t("dashboard.roi_guarantee.setup_pack_hours_label")}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-2xl text-green-600">
                        60
                      </div>
                      <div>
                        {t(
                          "dashboard.roi_guarantee.setup_pack_days_support_label"
                        )}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-2xl text-blue-600">4h</div>
                      <div>
                        {t("dashboard.roi_guarantee.setup_pack_response_label")}
                      </div>
                    </div>
                  </div>
                </div>
              </Surface>
            )}

            {isGuaranteeMode && (
              <Surface className="bg-gray-50 border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <HiTrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-3">
                      {t("dashboard.roi_guarantee.what_is_roi_title")}
                    </h2>
                    <p className="text-gray-700 mb-3">
                      {t("dashboard.roi_guarantee.what_is_roi_desc")}
                    </p>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600 mb-2 font-medium">
                        {t("dashboard.roi_guarantee.roi_formula")}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t("dashboard.roi_guarantee.roi_example", {
                          one: formatPrice(1, selectedCurrency),
                          three: formatPrice(3, selectedCurrency),
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </Surface>
            )}

            <Surface className="bg-blue-50 border-blue-200">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1">
                  <HiOfficeBuilding className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-800 mb-1">
                    {isGuaranteeMode
                      ? t(
                          "dashboard.roi_guarantee.business_qualification_title"
                        )
                      : t(
                          "dashboard.roi_guarantee.setup_pack_business_qualification_title"
                        )}
                  </h3>
                  <p className="text-sm text-blue-700 whitespace-pre-line">
                    {isGuaranteeMode
                      ? t("dashboard.roi_guarantee.business_qualification_desc")
                      : t(
                          "dashboard.roi_guarantee.setup_pack_business_qualification_desc"
                        )}
                  </p>
                </div>
              </div>
            </Surface>

            <Surface>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                {t("dashboard.roi_guarantee.what_you_get")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-shrink-0">{feature.icon}</div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">
                        {feature.title}
                      </h3>
                      {renderFeatureList(feature.description as string)}
                    </div>
                  </div>
                ))}
              </div>
            </Surface>

            <Surface className="bg-blue-50 border-blue-200">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1">
                  <HiPhone className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-800 mb-1">
                    {isGuaranteeMode
                      ? t("dashboard.roi_guarantee.support_program_title")
                      : t(
                          "dashboard.roi_guarantee.setup_pack_support_program_title"
                        )}
                  </h3>
                  <p className="text-sm text-blue-700">
                    {isGuaranteeMode
                      ? t("dashboard.roi_guarantee.support_program_desc")
                      : t(
                          "dashboard.roi_guarantee.setup_pack_support_program_desc"
                        )}
                  </p>
                </div>
              </div>
            </Surface>

            {isGuaranteeMode && (
              <Surface className="bg-blue-50 border-blue-200">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1">
                    <HiInformationCircle className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-800 mb-2">
                      {t("dashboard.roi_guarantee.refund_clarification_title")}
                    </h3>
                    <p className="text-sm text-blue-700 mb-3">
                      {t("dashboard.roi_guarantee.refund_clarification_desc", {
                        price: formattedPrice,
                      })}
                    </p>
                    <p className="text-sm text-blue-700">
                      {t("dashboard.roi_guarantee.compliance_note")}
                    </p>
                  </div>
                </div>
              </Surface>
            )}
          </div>

          <div className="lg:col-span-1">
            <Surface className="sticky top-24">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {isGuaranteeMode
                    ? t("dashboard.roi_guarantee.one_time_investment")
                    : t("dashboard.roi_guarantee.setup_pack_pricing_title")}
                </h3>

                <div className="mb-6">
                  {/* design-ok: prix, type d'affichage */}
                  <div className="text-3xl font-bold text-gray-900 mb-1">
                    {formattedPrice}
                  </div>
                  <div className="text-sm text-gray-600">
                    {t("dashboard.roi_guarantee.price_label")}
                  </div>
                </div>

                <div className="mb-6">
                  {isGuaranteeMode &&
                  !permissions.hasFeatureAccess("ROI_GUARANTEE") ? (
                    <div className="relative">
                      <CustomButton
                        disabled
                        variant="gray"
                        size="lg"
                        className="w-full blur-sm"
                        title={t("dashboard.roi_guarantee.add_to_plan")}
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-blue-600 text-white px-3 py-2 rounded-lg shadow-lg border">
                          <div className="text-xs font-semibold">
                            {t("dashboard.roi_guarantee.upgrade_to_annual")}
                          </div>
                          <div className="text-xs opacity-90">
                            {t("dashboard.roi_guarantee.save_discount_access")}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <CustomButton
                      variant="primary"
                      size="lg"
                      className="w-full"
                      onClick={handlePaymentRedirect}
                      title={t("dashboard.roi_guarantee.add_to_plan")}
                    />
                  )}
                </div>
              </div>
            </Surface>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROIGuarantee;
